class CitySearch extends HTMLElement {
  constructor() {
    super();
    this._onStateChange = this._onStateChange.bind(this);
    this._onCityChange = this._onCityChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);

    this._pendingToken = 0;
    this._existsCache = new Map();
    this._resolvedUrl = "";
    this._resolvedExists = false;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.selectState = this.querySelector("#city-search-state");
    this.selectCity = this.querySelector("#city-search-city");
    this.button = this.querySelector('button[type="submit"]');
    this.results = this.querySelector(".city-search__results");
    this.dataEl = this.querySelector("script[data-city-search-states]");

    if (!this.form || !this.selectState || !this.selectCity || !this.button || !this.results || !this.dataEl) return;

    this.states = this._readStatesFromDom();
    this.states.sort((a, b) => String(a.name).localeCompare(String(b.name), "en", { sensitivity: "base" }));

    this.stateByName = new Map(this.states.map((s) => [s.name, s]));

    this._fillStates();
    this._resetCity();
    this._setButtonEnabled(false);
    this._setMessage("Select a state to begin, please.", "info");

    this.selectState.addEventListener("change", this._onStateChange);
    this.selectCity.addEventListener("change", this._onCityChange);
    this.form.addEventListener("submit", this._onSubmit);
  }

  disconnectedCallback() {
    this.selectState?.removeEventListener("change", this._onStateChange);
    this.selectCity?.removeEventListener("change", this._onCityChange);
    this.form?.removeEventListener("submit", this._onSubmit);
  }

  _readStatesFromDom() {
    try {
      const json = this.dataEl.textContent || "[]";
      const data = JSON.parse(json);
      return Array.isArray(data) ? data.filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  _setMessage(text, status) {
    this.results.textContent = text || "";
    this.results.classList.remove("is-info", "is-loading", "is-success", "is-error");

    if (status === "loading") this.results.classList.add("is-loading");
    else if (status === "success") this.results.classList.add("is-success");
    else if (status === "error") this.results.classList.add("is-error");
    else this.results.classList.add("is-info");
  }

  _setButtonEnabled(enabled) {
    const ok = !!enabled;
    this.button.disabled = !ok;
    this.button.setAttribute("aria-disabled", String(!ok));
  }

  _setOptions(select, items, placeholder) {
    select.innerHTML = "";
    select.append(new Option(placeholder, ""));
    for (const { label, value } of items) select.append(new Option(label, value));
  }

  _fillStates() {
    this._setOptions(
      this.selectState,
      this.states.map((s) => ({ label: s.name, value: s.name })),
      "Select a state",
    );
  }

  _resetCity() {
    this._setOptions(this.selectCity, [], "Select a city");
    this.selectCity.disabled = true;
    this._resolvedUrl = "";
    this._resolvedExists = false;
  }

  _slugify(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9 ]+/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  }

  _buildUrl(_, cityName) {
    return `/pages/${this._slugify(cityName)}`;
  }

  async _urlExists(url) {
    if (this._existsCache.has(url)) return this._existsCache.get(url);

    let ok = false;
    try {
      const head = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (head.ok) ok = true;
      else {
        const get = await fetch(url, { method: "GET", cache: "no-store" });
        ok = get.ok;
      }
    } catch {
      ok = false;
    }

    this._existsCache.set(url, ok);
    return ok;
  }

  async _checkSelection() {
    const state = this.stateByName.get(this.selectState.value);
    const city = this.selectCity.value;

    if (!state) {
      this._setButtonEnabled(false);
      this._setMessage("Select a state to begin, please.", "info");
      this._resolvedUrl = "";
      this._resolvedExists = false;
      return;
    }

    if (!city) {
      this._setButtonEnabled(false);
      this._setMessage("Please select a city.", "info");
      this._resolvedUrl = "";
      this._resolvedExists = false;
      return;
    }

    const url = this._buildUrl(null, city);
    const token = ++this._pendingToken;

    this._setButtonEnabled(false);
    this._setMessage("Searching, please wait…", "loading");

    const exists = await this._urlExists(url);
    if (token !== this._pendingToken) return;

    this._resolvedUrl = url;
    this._resolvedExists = !!exists;

    if (exists) {
      this._setMessage("Found products in your city. Please click View Products to continue.", "success");
      this._setButtonEnabled(true);
    } else {
      this._setMessage("No products found for this city, please try another one.", "error");
      this._setButtonEnabled(false);
    }
  }

  _onStateChange() {
    this._pendingToken++;
    this._setButtonEnabled(false);
    this._resolvedUrl = "";
    this._resolvedExists = false;

    const state = this.stateByName.get(this.selectState.value);

    if (!state) {
      this._resetCity();
      this._setMessage("Select a state to begin, please.", "info");
      return;
    }

    const cities = [...(state.cities || [])].sort((a, b) => String(a).localeCompare(String(b), "en", { sensitivity: "base" }));

    this._setOptions(
      this.selectCity,
      cities.map((c) => ({ label: c, value: c })),
      "Select a city",
    );

    this.selectCity.disabled = false;
    this._setMessage("Please select a city.", "info");
  }

  _onCityChange() {
    this._checkSelection();
  }

  async _onSubmit(e) {
    e.preventDefault();

    if (!this._resolvedUrl || !this._resolvedExists) {
      await this._checkSelection();
    }

    if (this._resolvedUrl && this._resolvedExists) {
      window.location.href = this._resolvedUrl;
    }
  }
}

customElements.define("city-search", CitySearch);